/**
 * La conciliación de IVA vuelta CSV.
 *
 * SOLO FORMATO. Recibe lo que la pantalla ya tiene y lo pasa a texto: no consulta,
 * no recalcula y no decide nada. Si el archivo pudiera decir algo distinto de lo que
 * se está viendo, dejaría de servir para respaldar una cifra, que es para lo que se
 * descarga.
 *
 * TRES REGLAS QUE NO SON COSMÉTICAS:
 *
 *   1. VACÍO NO ES CERO. Un mes sin calcular, y los posteriores a la ruptura de la
 *      cadena, dejan la celda vacía. En un CSV esto pesa más que en pantalla: en
 *      cuanto alguien sume la columna en Excel, un cero inventado entra al total y ya
 *      no hay nada que delate de dónde salió. Una celda vacía se propaga como vacía.
 *   2. LOS IMPORTES SON NÚMEROS, NO TEXTO. Sin separador de miles y con punto
 *      decimal. Un `1,000.00` en un archivo separado por comas parte la fila en dos
 *      columnas; y aunque no la partiera, Excel lo leería como texto y la celda no
 *      sumaría ni entraría en una tabla dinámica. El formato es de quien abre el
 *      archivo, no de quien lo escribe.
 *   3. BOM DE UTF-8. Sin él, Excel en Windows abre el CSV como ANSI y los acentos de
 *      la razón social salen rotos. Es la misma lección que ya dejó escrita
 *      `ReporteDeclaracionesCsvService` en el backend.
 *
 * NO LLEVA TOTALES NI FILA DE ADVERTENCIA AL FINAL. Un bloque de resumen dentro de la
 * tabla rompe el autofiltro y aparece dentro de cualquier tabla dinámica, que es justo
 * lo que alguien va a querer usar. Lo que el archivo tiene que declarar —que es una
 * estimación— viaja en su nombre y en la columna `Estado`.
 */

/** BOM de UTF-8. */
const BOM = '﻿';

/** Excel espera CRLF. */
const FIN_DE_LINEA = '\r\n';

const COLUMNAS = [
  'Mes',
  'IVA trasladado',
  'IVA acreditable',
  'Saldo del mes',
  'Saldo a favor previo',
  'Acreditado',
  'IVA por pagar',
  'Saldo a favor al cierre',
  'Declarado (informativo)',
  'Estado',
  'Calculado',
];

/**
 * Un importe con dos decimales, o cadena vacía si no hay dato.
 *
 * `null` y `undefined` son "no se sabe" y salen vacíos. El cero solo aparece cuando
 * el dato es cero, que es un hecho distinto.
 */
const importe = (v) => (v === null || v === undefined || v === '' ? '' : Number(v).toFixed(2));

/**
 * Una celda escapada.
 *
 * Solo se entrecomilla cuando hace falta: un archivo con todo entre comillas es más
 * difícil de leer a ojo, y a ojo es como se revisa un CSV cuando algo no cuadra.
 */
const celda = (valor) => {
  const texto = valor === null || valor === undefined ? '' : String(valor);

  return /[",\r\n]/.test(texto) ? `"${texto.replace(/"/g, '""')}"` : texto;
};

/** Lo declarado en el acuse, como texto. Contraste, no cifra que participe. */
const declarado = (mes) => {
  if (!mes.declarado) return '';

  return `${importe(mes.declarado.importe)} ${mes.declarado.signo === 'favor' ? 'a favor' : 'a cargo'}`;
};

/**
 * El nombre del archivo.
 *
 * «estimada» va en el nombre porque es lo único que se ve SIN abrir el archivo, y un
 * archivo se desprende de la pantalla que lo advertía: viaja por correo, se adjunta y
 * se abre meses después.
 *
 * El RFC y la fecha van para que dos descargas de contribuyentes distintos no se
 * pisen en la carpeta de descargas.
 */
export const nombreArchivoConciliacion = (taxId, hoy = new Date()) => {
  const rfc = String(taxId || 'sin-rfc').trim().toUpperCase();
  const fecha = [
    hoy.getFullYear(),
    String(hoy.getMonth() + 1).padStart(2, '0'),
    String(hoy.getDate()).padStart(2, '0'),
  ].join('-');

  return `conciliacion-iva-estimada-${rfc}-${fecha}.csv`;
};

/**
 * El CSV de la conciliación.
 *
 * @param {object} datos
 * @param {Array} datos.meses  las filas que la pantalla muestra
 * @param {object} datos.cadena la cadena, para el arrastre de cada mes
 * @returns {string} el texto del archivo, con BOM
 */
export const construirCsvConciliacion = ({ meses = [], cadena = null } = {}) => {
  const arrastrePorPeriodo = new Map(
    (cadena?.desglose || []).map((fila) => [fila.period, fila])
  );

  const filas = meses.map((mes) => {
    const calculado = !!mes.calculado_en;
    // Un mes sin fila de cadena no está en cero: o no está calculado, o está
    // después de la ruptura. En los dos casos el arrastre sale vacío.
    const arrastre = arrastrePorPeriodo.get(mes.period) || null;

    return [
      mes.period,
      calculado ? importe(mes.iva_trasladado) : '',
      calculado ? importe(mes.iva_acreditable) : '',
      calculado ? importe(mes.saldo) : '',
      arrastre ? importe(arrastre.disponible_antes) : '',
      arrastre ? importe(arrastre.acreditado) : '',
      arrastre ? importe(arrastre.a_pagar) : '',
      arrastre ? importe(arrastre.saldo_despues) : '',
      declarado(mes),
      calculado ? 'Calculado' : 'Sin calcular',
      calculado ? String(mes.calculado_en).slice(0, 16) : '',
    ].map(celda).join(',');
  });

  return BOM + [COLUMNAS.map(celda).join(','), ...filas].join(FIN_DE_LINEA) + FIN_DE_LINEA;
};
